import { guessMimeFromUrl } from '@/utils/url';
import {
  descriptionId,
  getTemplate,
  getUiOptions,
  type FieldProps,
} from '@rjsf/utils';
import { UploadCloud } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../../Button';
import { Preview, ShFilePreview } from '../Components/ShFilePreview';

export const ShFileObjectField: React.FC<
  FieldProps<(File | string)[] | undefined>
> = ({
  idSchema: { $id: id },
  required,
  onChange,
  schema,
  formData,
  readonly,
  uiSchema,
  registry,
  idSchema,
}) => {
  const [previews, setPreviews] = useState<Preview[]>([]);

  /* build / clean up preview */
  useEffect(() => {
    console.log('FileObjectField useEffect', formData);

    // Throw error if formData is not an array
    if (formData) {
      if (!(formData instanceof Array)) {
        throw new Error(
          'FileObjectField expects formData to be a an array of File objects or strings',
        );
      }

      const objectUrls: string[] = [];

      const previews: Preview[] = formData.map((item) => {
        if (item instanceof File) {
          const url = URL.createObjectURL(item);
          objectUrls.push(url);
          return {
            url,
            mime: item.type || undefined,
          };
        } else if (typeof item === 'string') {
          return {
            url: item,
            mime: guessMimeFromUrl(item),
          };
        } else {
          throw new Error(
            'FileObjectField expects formData to be an array of File objects or strings',
          );
        }
      });

      setPreviews(previews);

      return () => {
        console.log('Cleaning up object URLs:', objectUrls);
        objectUrls.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      };
    }
  }, [formData]);

  const removeItem = (idx: number) => {
    onChange((formData || []).filter((_, i) => i !== idx));
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange([...(formData || []), ...Array.from(event.target.files || [])]);
  };

  const { globalUiOptions } = registry;
  const uiOptions = getUiOptions(uiSchema, globalUiOptions);
  const description = uiOptions.description ?? schema.description;
  const DescriptionFieldTemplate = getTemplate(
    'DescriptionFieldTemplate',
    registry,
    uiOptions,
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center">
        {schema.title && (
          <label htmlFor={id} className="text-sm font-medium">
            {schema.title}
            {required && <span className="ml-0.5 text-red-600">*</span>}
          </label>
        )}
        {description && (
          <DescriptionFieldTemplate
            id={descriptionId(idSchema)}
            description={description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        )}
      </div>

      {readonly && previews.length === 0 ? (
        <div className="text-muted-foreground">Empty</div>
      ) : (
        previews.map((preview, idx) => (
          <ShFilePreview
            key={idx}
            preview={preview}
            readonly={readonly}
            onRemove={() => removeItem(idx)}
          />
        ))
      )}
      {!readonly && (
        <>
          <input
            id={id}
            type="file"
            multiple
            accept={schema.accept as string | undefined}
            onChange={onInputChange}
            className="sr-only" // tailwind's visually‑hidden utility
            disabled={readonly}
          />
          <Button
            variant="secondary"
            asChild
            disabled={readonly}
            className="max-w-[15em]"
          >
            <label htmlFor={id} className="cursor-pointer">
              <UploadCloud className="mr-2 h-4 w-4" />
              Upload file
            </label>
          </Button>
        </>
      )}
    </div>
  );
};
